import React, { useEffect, useState } from "react";
import { Box, Paper, Grid, TextField, MenuItem, Button, Snackbar, Alert, Drawer, List, ListItem, ListItemText } from "@mui/material";
import { useMasterData } from "../../../hooks/useMasterData";
import { studentLogApi, StudentLogItem } from "./studentLogApi";
import StudentLogTable from "./StudentLogTable";

const StudentLogPage: React.FC = () => {
  const { masters, fetchAcademicSessions } = useMasterData();
  const [filters, setFilters] = useState({ studentId: "", sessionId: "", module: "", action: "", dateFrom: "", dateTo: "" });
  const [rows, setRows] = useState<StudentLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({
    open: false,
    message: "",
    severity: "info",
  });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<StudentLogItem[]>([]);
  const [pageState, setPageState] = useState({ skip: 0, take: 20 });

  useEffect(() => {
    fetchAcademicSessions();
  }, []);

  useEffect(() => {
    // Auto-load logs on first mount
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = async () => {
    try {
      const res = await studentLogApi.list({ ...filters, ...pageState });
      setRows(res.items);
      setTotal(res.total);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to load logs", severity: "error" });
    }
  };

  const reset = () => {
    const resetFilters = { studentId: "", sessionId: "", module: "", action: "", dateFrom: "", dateTo: "" };
    setFilters(resetFilters);
    setPageState({ skip: 0, take: 20 });
    setRows([]);
    setTotal(0);
  };

  const viewHistory = async (studentId: string) => {
    try {
      const res = await studentLogApi.historyByStudent(studentId);
      setHistory(res);
      setHistoryOpen(true);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to load history", severity: "error" });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ fontSize: 22, fontWeight: 600, mb: 2 }}>Student Log</Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <TextField label="Student ID" fullWidth value={filters.studentId} onChange={(e) => setFilters({ ...filters, studentId: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select fullWidth label="Session" value={filters.sessionId} onChange={(e) => setFilters({ ...filters, sessionId: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              {masters.academicSessions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="Module" fullWidth value={filters.module} onChange={(e) => setFilters({ ...filters, module: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="Action" fullWidth value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Date From"
              type="date"
              fullWidth
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Date To"
              type="date"
              fullWidth
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="contained" fullWidth onClick={search}>
              Search
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth onClick={reset}>Reset</Button>
          </Grid>
        </Grid>
      </Paper>

      <StudentLogTable rows={rows} onViewHistory={viewHistory} />

      <Drawer anchor="right" open={historyOpen} onClose={() => setHistoryOpen(false)}>
        <Box sx={{ width: 420, p: 2 }}>
          <Box sx={{ fontWeight: 600, mb: 2 }}>Student Activity Timeline</Box>
          <List dense>
            {history.map((h) => (
              <ListItem key={h.id} divider>
                <ListItemText
                  primary={`${new Date(h.createdAt).toLocaleString()} — ${h.module} / ${h.action}`}
                  secondary={h.description}
                />
              </ListItem>
            ))}
            {history.length === 0 && <ListItem><ListItemText primary="No history found" /></ListItem>}
          </List>
        </Box>
      </Drawer>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentLogPage;
